import { useEffect, useRef, useState, type JSX } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { RevvlyStatusIcon } from '@admin-platform/ui/components';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '../Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../DropdownMenu/DropdownMenu';
import { usePlatformStatus } from '../../hooks/usePlatformStatus';
import { Skeleton } from '../Skeleton/Skeleton';

import { APP_RELEASE_VERSION } from './appRelease';
import { ReleaseItems } from './ReleaseItems';
import { releaseDate } from './releaseNoteFormat';
import {
  MARK_RELEASE_NOTES_SEEN_MUTATION,
  RELEASE_NOTES_POLL_MS,
  RELEASE_NOTES_QUERY,
  RELEASE_NOTES_UNREAD_SUMMARY_QUERY,
  WHATS_NEW_PATH,
} from './releaseNotesOps';
import { iconLabel, iconStateFor } from './revvlyIconState';
import styles from './WhatsNewButton.module.css';

/** Releases listed in the panel; the rest is on the full page. */
const PANEL_SIZE = 8;
/** Below the md breakpoint the icon opens the full page instead of a panel (as the bell does). */
const PHONE_QUERY = '(max-width: 767px)';

function isPhone(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(PHONE_QUERY).matches
    : false;
}

/**
 * The Revvly icon beside the notification bell: what is happening to Revvly, the product. It
 * shows a service problem first (from `GET /health/status`), else an unread release (an arrow
 * for a patch, a new layer on the stack for a minor or major), else the plain tile. Hovering names
 * the state; the panel lists the latest releases, under the status when something is wrong.
 *
 * Not part of the bell on purpose. The bell is per workspace and per recipient and expires its
 * items; this is the platform's own news, the same for everyone, read by a per-person cursor.
 * The glyph is the product, never the AI: Brynn (wave + spark) is Ask Revvly's mark.
 */
export function WhatsNewButton(): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const variables = { upToVersion: APP_RELEASE_VERSION ?? null };

  const platform = usePlatformStatus(APP_RELEASE_VERSION ?? null);
  const countQuery = useQuery(RELEASE_NOTES_UNREAD_SUMMARY_QUERY, {
    variables,
    fetchPolicy: 'cache-and-network',
    pollInterval: RELEASE_NOTES_POLL_MS,
  });
  const listQuery = useQuery(RELEASE_NOTES_QUERY, {
    variables: { ...variables, first: PANEL_SIZE },
    fetchPolicy: 'cache-and-network',
    skip: !open,
  });
  const [markSeen] = useMutation(MARK_RELEASE_NOTES_SEEN_MUTATION);

  const summary = countQuery.data?.releaseNotesUnreadSummary;
  const unread = summary?.count ?? 0;
  const iconState = iconStateFor(platform.state, { count: unread, kind: summary?.kind });
  const label = iconLabel(iconState, platform, summary?.version);
  const trouble = iconState === 'down' || iconState === 'degraded';
  // Releases with nothing to say to a customer (a silent patch) are in the log, not the panel.
  const notes = (listQuery.data?.releaseNotes.nodes ?? []).filter((n) => n.items.length > 0);

  // Reading is marked once the list has arrived, never at the moment of opening: the server
  // decides which notes are unread from the person's position, so marking first would race the
  // list and show it with nothing highlighted. The list is not refetched afterwards, so what was
  // new stays highlighted until the panel closes.
  const markedThisOpen = useRef(false);
  const refetchCount = countQuery.refetch;
  useEffect(() => {
    if (!open) {
      markedThisOpen.current = false;
      return;
    }
    if (markedThisOpen.current || !listQuery.data) return;
    markedThisOpen.current = true;
    if (unread === 0 && !listQuery.data.releaseNotes.unreadCount) return;
    void markSeen({ variables })
      .then(() => refetchCount())
      .catch(() => undefined);
    // `variables` is a constant for the life of the build.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, listQuery.data, unread, markSeen, refetchCount]);

  const onOpenChange = (next: boolean) => {
    if (next && isPhone()) {
      navigate(WHATS_NEW_PATH);
      return;
    }
    setOpen(next);
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          forIconOnly
          aria-label={label}
          className={styles.Trigger}
        >
          <RevvlyStatusIcon state={iconState} />
          {!open && (
            <span className={styles.Label} aria-hidden="true">
              {label}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className={styles.Panel}
        aria-label="What's new"
      >
        <div className={styles.Header}>
          <span className={styles.HeaderTitle}>What&apos;s new</span>
        </div>
        {trouble && (
          <p
            className={iconState === 'down' ? styles.StatusDown : styles.StatusDegraded}
            role="status"
          >
            <span className={styles.StatusTitle}>{label}</span>
            {platform.incident?.message && (
              <span className={styles.StatusMessage}>{platform.incident.message}</span>
            )}
          </p>
        )}
        <div className={styles.List}>
          {listQuery.loading && notes.length === 0 ? (
            <div className={styles.Loading} aria-busy="true">
              <Skeleton height="2.5rem" />
              <Skeleton height="2.5rem" />
            </div>
          ) : listQuery.error && notes.length === 0 ? (
            <p className={styles.Message} role="status">
              Releases could not be loaded.{' '}
              <Button variant="ghost" size="xs" onClick={() => void listQuery.refetch()}>
                Try again
              </Button>
            </p>
          ) : notes.length === 0 ? (
            <p className={styles.Message} role="status">
              Nothing to show yet.
            </p>
          ) : (
            notes.map((note) => (
              <div key={note.id} className={note.unread ? styles.EntryUnread : styles.Entry}>
                <div className={styles.EntryHead}>
                  <span className={styles.Version}>{note.version}</span>
                  <span className={styles.Date}>{releaseDate(note.releasedAt)}</span>
                </div>
                {note.title && <p className={styles.EntryTitle}>{note.title}</p>}
                <ReleaseItems items={note.items} limit={3} />
              </div>
            ))
          )}
        </div>
        <DropdownMenuItem asChild className={styles.Footer}>
          <Link to={WHATS_NEW_PATH}>See all releases</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
